/* eslint-disable */

// WebcamJS __VERSION__
// Webcam library for capturing JPEG/PNG images in JavaScript
// Author: Joseph Huckaby: http://github.com/jhuckaby
// Based on JPEGCam: http://code.google.com/p/jpegcam/
// Copyright (c) 2012 - 2016 Joseph Huckaby
// Licensed under the MIT License

const React = require('react');
const is = require('next-is');
const { createRoot } = require('react-dom/client');

const { waitFor } = require('sf/helpers');

const helpers = require('./helpers');
const model = require('./models/model');
const videoRecorder = require('./video-recorder').default;

const UploadFallbackNode = require('./components/UploadFallbackNode');
const WebcamContainer = require('./components/WebcamContainer');
const { canvasToBlobURL, drawOnCanvas, getImage } = require('sf/helpers/canvas');

const {
	createElement,
	debouncePromise,
	measureBlur,
	setPrefixedStyle,
	whenDOMReady,
} = helpers;



// when image size is larger than declared dest_width/dest_height
// then we allow for a margin. Here we allow 25% larger photos.
const MAX_IMAGE_SIZE_DEVIATION = 1.25;

let _userMedia;
const navigator = global.navigator || {};
const ua = navigator.userAgent || '';
const STANDARD_RESOLUTIONS = [
	480, 544, 576, 640, 720, 724, 792, 800, 864, 936, 960, 1024, 1080, 1180, 1200, 1280, 1440, 1536, 1600, 1920, 2048, 2160, 2560, 2880, 3032, 3072, 3264
];

var isDesktop = !is.mobile() && !is.tablet();
const isFirefox = is.firefox();

// There are specific problems on some devices (Google Pixel, new Samsungs, etc).
// Forcing WebRTC detection mode seems to fix NotReadable issue.
//
// Desktops can't use WebRTC detection mode because facingMode: 'user' / 'environment' always
// brings default camera even on Microsoft Surface Pro tablet.
// Firefox: this browser asks user for camera permission, every time. We force one detection mode there.
var forceWebrtcDetectionMode = /*!isDesktop || */isFirefox;

// declare error types

function WebcamError() {
	var temp = Error.apply(this, arguments);
	temp.name = this.name = "WebcamError";
	this.stack = temp.stack;
	this.message = temp.message;
}

function IntermediateInheritor() {}
IntermediateInheritor.prototype = Error.prototype;

WebcamError.prototype = new IntermediateInheritor();

const CAM_BACK = 'CAM_BACK';
const CAM_FRONT = 'CAM_FRONT';
const CAPTURE_MODE_PHOTO = 'CAPTURE_MODE_PHOTO';
const CAPTURE_MODE_VIDEO = 'CAPTURE_MODE_VIDEO';
const DETECTION_MODE_LABELS = 'DETECTION_MODE_LABELS';
const DETECTION_MODE_FACING_MODE = 'DETECTION_MODE_FACING_MODE';
const MAX_CAPTURE_TIMEOUT = 6000; // 6s

const cameraWidthCache = {
	landscape: {
		[CAM_FRONT]: 0,
		[CAM_BACK]: 0,
	},
	portrait: {
		[CAM_FRONT]: 0,
		[CAM_BACK]: 0,
	}
};

class Webcam {
	constants = {
		CAM_BACK,
		CAM_FRONT,
		CAPTURE_MODE_PHOTO,
		CAPTURE_MODE_VIDEO,
		DETECTION_MODE_LABELS,
		DETECTION_MODE_FACING_MODE,
		STANDARD_RESOLUTIONS,

		MODE_ACCEPTS: {
			CAPTURE_MODE_PHOTO: 'image/*',
			CAPTURE_MODE_VIDEO: 'video/*',
		},
		WEBRTC_CAMERAS: {
			CAM_BACK: 'environment',
			CAM_FRONT: 'user',
		},
		CAMERA_MODE_NONE: 'none',
		CAMERA_MODE_FILE: 'file-fallback',
		CAMERA_MODE_WEBRTC: 'webrtc'
	};

	errors = {
		WebcamError,
	};

	version = '__VERSION__';

	init() {
		const params = this.params.get();
		// initialize, check for getUserMedia support
		// Setup getUserMedia, with polyfill for older browsers
		// Adapted from: https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
		this.mediaDevices = navigator.mediaDevices || false;
		let userMedia = params.userMedia && !!this.mediaDevices && !!URL;

		// Older versions of firefox (< 21) apparently claim support but user media does not actually work
		if (isFirefox) {
			if (parseInt(RegExp.$1, 10) < 21) userMedia = null;
		}
		this.params.set('userMedia', userMedia);

		// Make sure media stream is closed when navigating away from page
		if (userMedia) {
			detectVideoInputs(this.mediaDevices);
			addEventListener('beforeunload', this.reset.bind(this));

			var locked = false;
			addEventListener('orientationchange', () => {
				if (
					locked // prevents multiple attach on rapid orientationchange
					|| !params.load // Webcam not loaded yet
					|| params.videoRecording // Recording in progress
					|| params.captureInProgress // Recording in progress
				) return;

				if (params.load) {
					locked = true;
					// video needs to be reinitialised after screen rotation so taken pictures aren't
					// rotated.
					webcam.reattach().then(() => { locked = false; });
				}
			});
		}
	}

	getVideo() {
		return this.params.get('cameraVideoElement');
	}

	reattach(newContainer) {
		return new Promise((resolve) => {
			const container = newContainer || this.container;
			this.reset().then(() => {
				this.attach(container, resolve);
			});
		});
	}

	checkIfCanCapture() {
		// isWebRTCBrowser: for every other browser we need to provide file fallback
		const isWebRTCBrowser = this.params.get('userMedia');
		//  isWebRTCCameraWorking: false when camera did not start by some reason - e.g. access not granted.
		const isWebRTCCameraWorking = !!(isWebRTCBrowser && this.stream && this.stream.active);

		return !isWebRTCBrowser || isWebRTCCameraWorking;
	}

	switchCamera = whenDOMReady((requestedCamera) => {
		return new Promise((resolve) => {
			const paramsData =  this.params.get();

			if (process.env.NODE_ENV !== 'production' && paramsData.verbose) {
				console.log('switchCamera called', requestedCamera);
			}

			if (!paramsData.cameraDetectionMode && paramsData.userMedia) {
				return detectVideoInputs(this.mediaDevices).then(() => {
					this.switchCamera(requestedCamera).then(resolve);
				});
			}

			const resultCamera = requestedCamera
				|| (paramsData.camera === CAM_FRONT ? CAM_BACK : CAM_FRONT);
			const isCameraIdSet = Number.isFinite(paramsData.cameraId);
			let resultCameraId = isCameraIdSet
				? paramsData.cameraId
				: -1;

			const cameraIdsByLabel = getCameraIdByLabel(resultCamera, true); // select camera by label
			if (isCameraIdSet && resultCamera === paramsData.camera && cameraIdsByLabel.includes(resultCamera)) {
				return resolve();
			}

			const cameraIdByLabel = cameraIdsByLabel[0]; // select camera by label

			resultCameraId = Number.isFinite(cameraIdByLabel)
				? cameraIdByLabel
				: (resultCameraId + 1) % paramsData.cameraInfs.length; // select next camera

			this.params.set({
				camera: resultCamera, // CAM_FRONT || CAM_BACK
				cameraId: resultCameraId,
			});
			resolve();
		});
	});

	attach = whenDOMReady((container, callback = () => {}) => {
		if (this.container === container) {
			return this.reattach(container).then(callback);
		}

		// create webcam preview and attach to DOM element
		// pass in actual DOM reference
		if (!container) {
			return this.dispatch('error', new WebcamError("Could not locate DOM element to attach to."));
		}
		if (this.params.get('cameraId') === undefined) {
			return this.switchCamera(this.params.get('camera')).then(() => {
				this.attach(container, callback);
			});
		}

		if (process.env.NODE_ENV !== 'production' && this.params.get('verbose')) {
			console.log('WebcamJS Attaching', container);
		}

		// set width/height if not already set
		this.params.set({
			width: this.params.get('width') || container.offsetWidth,
			height: this.params.get('height') || container.offsetHeight,
			dest_width: this.params.get('dest_width') || this.params.get('width'),
			dest_height: this.params.get('dest_height') || this.params.get('height'),
		});

		// make sure we have a nonzero width and height at this point
		if (process.env.NODE_ENV !== 'production' && (!this.params.get('width') || !this.params.get('height'))) {
			return this.dispatch('error', new WebcamError("No width and/or height for webcam.  Please call set() first, or attach to a visible element."));
		}

		let userMedia = _userMedia === undefined ? this.params.get('userMedia') : _userMedia;

		if (this.params.get('force_file')) {
			_userMedia = userMedia;
			userMedia = false;
		}
		this.params.set('userMedia', userMedia);

		this.container = container;

		const component = (
			<WebcamContainer
				webcam={ this }
				onPegRef={(pegNode) => { this.peg = pegNode; }}
			/>
		);

		this.root = createRoot(container);
		this.root.render(component);

		callback();
	});

	reset = debouncePromise(() => {
		return new Promise((resolve) => {
			if (process.env.NODE_ENV !== 'production' && this.params.get('verbose')) {
				console.log('WebcamJS reset');
			}

			if (this.params.get('videoRecording') || this.params.get('captureInProgress')) {
				throw new Error('Can\'t reset camera while video is recording or capturing.');
			}

			// shutdown camera, reset to potentially attach again
			if (this.preview_canvas) this.unfreeze();

			// attempt to fix issue #64
			this.unflip();
			var stream = this.stream;
			if (this.params.get('userMedia')) {
				if (stream) {
					if (stream.getVideoTracks) {
						// get video track to call stop on it
						const tracks = stream.getVideoTracks();
						if (tracks && tracks[0] && tracks[0].stop) tracks[0].stop();
					}
					if (stream.getAudioTracks) {
						const tracks = stream.getAudioTracks();
						if (tracks && tracks[0] && tracks[0].stop) tracks[0].stop();
					}
					if (stream.stop) {
						// deprecated, may be removed in future
						stream.stop();
					}
				}

				delete this.stream;
			}

			if (this.container) {
				try {
					this.root.unmount();
				} catch (e) { /* noop */ }
				delete this.container;
				delete this.root;
			}

			this.params.resetState();
			delete this.fallbackImage;

			function resolveWhenUnloaded() {
				setTimeout(() => {
					if (stream && stream.active) { // stoping stream might take some time
						resolveWhenUnloaded();
					} else {
						resolve();
					}
				}, 150);
			}

			resolveWhenUnloaded();
		});
	});

	set(...args) { this.params.set(...args); }

	on(name, callback) {
		// set callback hook
		this.params.on(name.replace(/^on/, ''), callback);
	}

	off(name, callback) {
		// remove callback hook
		this.params.off(name.replace(/^on/, ''), callback);
	}

	dispatch(_name, ...args) {
		if (this.params.get('verbose')) {
			console.info('📷 webcam dispatch', _name, ...args);
		}

		const name = _name.replace(/^on/, '');

		if (args.length > 1) {
			throw new Error('Invalid arguments count passed to webcam.dispatch');
		}

		// Special case when no error handlers were added.
		if (!this.params.dispatch(name, ...args) && name == 'error') {
			var message;
			if (args[0] instanceof WebcamError) {
				message = args[0].message;
			} else {
				message = "Could not access webcam: " + args[0].name + ": " +
					args[0].message + " " + args[0].toString();
			}

			// default error handler if no custom one specified
			alert("Webcam.js Error: " + message);
		}

		return false; // no hook defined
	}

	getUploadFallbackNode(communicationChannel = this.container.dataset.channel, extraProps = {}) {
		return (
			<UploadFallbackNode
				webcam={ this }
				communicationChannel={ communicationChannel }
				{ ...extraProps }
			/>
		);
	}

	handleImageInput = (e) => { // snap for the fallback
		const target = e.target;
		if (!target.files || !target.files.length) {
			delete this.fallbackImage;
			return;
		}

		this.fallbackImage = waitFor(new Promise((resolve, reject) => {
			getImage(target.files[0]).then((img) => {
				if (target) target.value = '';

				var channel = target.dataset.channel ? ':' + target.dataset.channel : '';
				this.getSnapResult(img).then((snapResult) => {
					this.dispatch(`imageSelected${channel}`, snapResult);
					resolve(snapResult);
					img.remove();
				}).catch(reject);
			}).catch(reject);
		}));
	};

	freeze() {
		// show preview over the video. Video stream is still active.
		const params = this.params.get();

		// kill preview if already active
		this.unfreeze();

		// must unflip container as preview canvas will be pre-flipped
		this.unflip();

		const video = this.getVideo();
		if (!video) return;

		// create canvas for holding preview
		const preview_canvas = this.preview_canvas = createElement('canvas', {
			className: `${params.cssPrefix}__preview`
		});

		// FAST image capture for freeze function
		drawOnCanvas(video, preview_canvas);
		this.flip(preview_canvas);
		this.peg.parentNode.insertBefore(preview_canvas, this.peg);
	}

	unfreeze() {
		// cancel preview and resume live video feed
		if (this.preview_canvas) {
			// remove preview canvas
			this.preview_canvas.remove();
			delete this.preview_canvas;

			// re-flip if we unflipped before
			this.flip();
		}
	}

	/**
	 * flip container horiz (mirror mode) if desired
	 */
	flip(flip = true) {
		const video = this.getVideo();

		if (video) {
			const params = this.params.get();

			if (flip
				&& (
					!params.flip_horiz
					|| params.camera === CAM_BACK && !params.flip_horiz_back
				)
			) {
				return this.unflip();
			}

			setPrefixedStyle(video, 'transform', `scaleX(${flip ? '-1' : '1'})`);
			video.style.filter = flip ? 'FlipH' : '';
			video.style.msFilter = flip ? 'FlipH' : '';
		}
	}

	/**
	 * unflip container horiz (mirror mode) if desired
	 */
	unflip() {
		return this.flip(false);
	}

	/**
	 * getSnapResult takes an input image
	 * - scales it if needed
	 * - converts it to the proper output_format
	 *
	 * @param  {Video, ImageBitmap, Image or Canvas} input
	 *
	 * @return {Canvas, Blob or base64 image string}
	 */
	async getSnapResult(input) { /* private */
		const canvas = drawOnCanvas(input, {
			scale: getScale(
				input.videoWidth || input.naturalWidth || input.width,
				input.videoHeight || input.naturalHeight || input.height,
			),
		});

		const {
			output_format,
			image_format,
			jpeg_quality,
		} = this.params.get();

		switch (output_format) {
			case 'canvas':
				return canvas;
			case 'blob':
				if (canvas.toBlob) {
					// every browser but Edge
					const result = canvasToBlobURL(canvas, `image/${image_format}`, jpeg_quality / 100);
					result.finally(() => canvas.remove());

					return result;
				}
			case 'base64': // when blob not available, fallbacks to base64
				const result = canvas.toDataURL(`image/${image_format}`, jpeg_quality / 100);
				canvas.remove();
				return result;
			default:
				throw new Error(`Unknown output_format: ${output_format}`);
		}
	}

	/**
	 * Snap a photo to a new or existing canvas
	 *
	 * @return {Promise<Canvas>}
	 */
	snap() { /* public */
		return new Promise((resolve, reject) => {
			const params = this.params.get();
			// take snapshot and return image data uri
			if (!params.load) {
				return reject(
					this.dispatch('error', new WebcamError("Webcam is not loaded yet"))
				);
			}

			const video = this.getVideo();
			var finalizeSnap = (canvas) => {
				return this.getSnapResult(canvas).then(resolve).catch(reject);
			};

			// grab image frame from userMedia
			if (
				params.userMedia
				&& params.use_ImageCapture
				&& (
					params.use_ImageCapture_takePhoto
					|| params.use_ImageCapture_grabFrame
				)
			) {
				const retryFunc = () => {
					// first try: takePhoto
					// second try: grabFrame
					// third try: grab a frame old way
					this.params.set('use_ImageCapture_takePhoto', false);
					this.params.set('use_ImageCapture_grabFrame', params.use_ImageCapture_grabFrame ? false : true);
					this.snap()
						.then(resolve)
						.catch(reject);
				};
				const imageCapture = new ImageCapture(this.stream.getVideoTracks()[0]);

				// after 10 sec, take a frame, instead of waiting for experimental imageCapture API

				let isTimedOut = false;
				const captureTimeout = setTimeout(() => {
					isTimedOut = true;
					retryFunc();
				}, MAX_CAPTURE_TIMEOUT);

				return imageCapture[params.use_ImageCapture_grabFrame ? 'grabFrame' : 'takePhoto'](
					// {
					// NOTE: since Chrome 68 MDN documentation is wrong. You can't pass any arguments to imageCapture.takePhoto
					//       because it will throw an error:
					//       DOMException: setOptions failed
					//
					// 	imageWidth: params.dest_width,
					// 	imageHeight: params.dest_height,
				)
					.then((blobOrBitmap) => {
						clearTimeout(captureTimeout);
						if (isTimedOut) return;
						video?.pause();
						if (blobOrBitmap instanceof ImageBitmap) {
							finalizeSnap(blobOrBitmap)
								.finally(() => blobOrBitmap.close());
						} else {
							waitFor(getImage(blobOrBitmap)).then((img) => {
								finalizeSnap(img);
							})
						}

					}).catch((err) => {
						clearTimeout(captureTimeout);
						if (isTimedOut) return;
						console.warn(`${params.use_ImageCapture_grabFrame ? 'grabFrame' : 'takePhoto'} didn't work. capture a video frame.`, err);

						retryFunc();
					});
			} else if (params.userMedia) {
				video.pause();

				return finalizeSnap(video);
			} else if (params.enable_file_fallback) {
				if (this.fallbackImage) {
					return this.fallbackImage.then((image) => {
						getImage(image).then((img) => {
							finalizeSnap(img);
							img.remove();
						})
					});
				}

				return reject(this.dispatch('error', "Select picture first."));
			}

			return reject(this.dispatch('error', "Webcam has encountered an unknown error."));
		}).then((result) => {
			// Resume. Only usable for non-delayed start.
			this.getVideo()?.play();

			return result;
		});
	}
};

const webcam = new Webcam();
webcam.params = model(webcam);

whenDOMReady(webcam.init.bind(webcam)).call();

const enumerateDevices = debouncePromise((mediaDevices) => mediaDevices.enumerateDevices());
function detectVideoInputs(mediaDevices) {
	return new Promise((resolve, reject) => {
		enumerateDevices(mediaDevices).then(function(devices) {
			var labels = '';
			const cameraInfs = devices.reduce((acc, device) => {
				if (
					device.kind === 'videoinput'
					&& !`${device.label}`.toLowerCase().includes(' ir camera')
				) {
					const deviceInfo = webcam.helpers.cloneInputDeviceInfo(device);
					labels += device.label || '';
					if (device.getCapabilities) {
						deviceInfo.capabilities = device.getCapabilities();
					}
					acc.push(deviceInfo);
				}

				return acc;
			}, []);

			cameraInfs.sort(function(a, b) {
				/**
				 * Sort cameras. As for 01-2022 camera infs are in invalid order.
				 * Sorting helps with selecting proper (non-wide angle) camera
				 */
				if (a.label < b.label) {
					return -1;
				}
				if (a.label > b.label) {
					return 1;
				}
				if (a.capabilities && b.capabilities) {
					// in case when label is the same for multiple cameras (LG-V40), we select
					// camera with higher resolution
					const aResolution = (a.capabilities.height && a.capabilities.height.max || 0)
						* (a.capabilities.width && a.capabilities.width.max || 0);
					const bResolution = (b.capabilities.height && b.capabilities.height.max || 0)
						* (b.capabilities.width && b.capabilities.width.max || 0);

					if (aResolution < bResolution) {
						return -1;
					}
					if (a.label > b.label) {
						return 1;
					}
				}
				return 0;
			});

			// cameraDetectionMode works better and produces better picture in webrtc mode
			const isLabels = labels.length && !is.iOS() ? true : false;
			const cameraDetectionMode = webcam.params.get('cameraDetectionMode');
			let newDetectionMode = isLabels ? DETECTION_MODE_LABELS : DETECTION_MODE_FACING_MODE;
			newDetectionMode = forceWebrtcDetectionMode && DETECTION_MODE_FACING_MODE || newDetectionMode;

			webcam.params.set({
				'cameraInfs': cameraInfs,
				'cameraDetectionMode': newDetectionMode
			}).then(() => {
				resolve(cameraInfs);
			}, reject);
		}).catch(reject);

	});
}

// inspired by https://unpkg.com/browse/scandit-sdk@4.6.1/src/lib/cameraAccess.ts
//             https://unpkg.com/browse/scandit-sdk@5.9.2/build/main/lib/cameraAccess.js
const BACK_CAMERA_KEYWORDS = [
	"rear",
	"back",
	"rück",
	"arrière",
	"trasera",
	"trás",
	"traseira",
	"posteriore",
	"后面",
	"後面",
	"背面",
	"后置", // alternative
	"後置", // alternative
	"背置", // alternative
	"задней",
	"الخلفية",
	"후",
	"arka",
	"achterzijde",
	"หลัง",
	"baksidan",
	"bagside",
	"sau",
	"bak",
	"tylny",
	"takakamera",
	"belakang",
	"אחורית",
	"πίσω",
	"spate",
	"hátsó",
	"zadní",
	"darrere",
	"zadná",
	"задня",
	"stražnja",
	"belakang",
	"बैक",
];

function getCameraIdByLabel(
	_cam, // CAM_FRONT or CAM_BACK
	getAll = false,
	cameraInfs = webcam.params.get('cameraInfs'),
) {
	const resultCameraIds = [];

	cameraInfs.forEach(function (cameraInf, id) {
		var label = cameraInf.label && cameraInf.label.toLowerCase() || '';
		const facingMode = cameraInf.capabilities && cameraInf.capabilities.facingMode || [];
		if (facingMode.length) {
			// use actual capabilities provided by browser over labels
			if (
				facingMode.includes(_cam === CAM_FRONT ? 'user' : 'environment')
				&& !label.includes(' ir camera')
			) {
				resultCameraIds.push(id);
			}
		} else if (
			_cam === CAM_FRONT && (label.includes('front') || label.includes('face'))
			|| _cam === CAM_BACK && BACK_CAMERA_KEYWORDS.some((keyword) => label.includes(keyword))
		) {
			// labels based detection for older browsers
			if (!label.includes(' ir camera')) {
				resultCameraIds.push(id);
			}
		}
	});

	return getAll ? resultCameraIds : resultCameraIds[0];
}

/**
 * getScale converts input image dimensions into scale
 * parameter that can be passed to drawOnCanvas.
 * Maximum result value is 1. we don't allow upscaling
 *
 * Result is based on dest_height, dest_width and MAX_IMAGE_SIZE_DEVIATION.
 *
 * Example results
 * dest_height = 1600
 * dest_width = 1600
 * MAX_IMAGE_SIZE_DEVIATION = 1.20;
 *
 * getScale(600, 600)    =>  1
 * getScale(1600, 1600)  =>  1
 * getScale(1920, 1920)  =>  1 (1600x1.20 = 1920)
 * getScale(2800, 2800)  =>  0.6857142857142857
 * getScale(6000, 6000)  =>  0.32
 *
 * @param  {number} inputWidth
 * @param  {number} inputHeight
 * @return {number}
 */
function getScale(inputWidth, inputHeight) {
	const params = webcam.params.get();
	const inputSize = inputWidth * inputHeight;
	const destSize = (params.dest_height * MAX_IMAGE_SIZE_DEVIATION)
		* (params.dest_width * MAX_IMAGE_SIZE_DEVIATION);

	return inputSize > destSize
		? Math.sqrt(destSize) / Math.sqrt(inputSize)
		: 1;
}

webcam.helpers = Object.assign(helpers, {
	videoRecorder: videoRecorder.init(webcam),
	detectVideoInputs,
	getCameraIdByLabel,
});

module.exports = webcam;
